import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, withModifiers as _withModifiers, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock, normalizeStyle as _normalizeStyle, withKeys as _withKeys, createTextVNode as _createTextVNode, Teleport as _Teleport, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-40ec8027"),n=n(),_popScopeId(),n)
const _hoisted_1 = {
  key: 0,
  class: "px-3 py-4 text-xs text-neutral50"
}
const _hoisted_2 = {
  class: "min-w-0 flex-1 truncate text-[13px] font-bold text-textPrimary",
  "aria-live": "polite",
  "data-testid": "send-record-attachment-name"
}
const _hoisted_3 = { class: "flex flex-none gap-1" }
const _hoisted_4 = ["href"]
const _hoisted_5 = {
  key: 0,
  class: "p-3"
}
const _hoisted_6 = ["data-testid"]
const _hoisted_7 = ["role", "tabindex", "aria-label", "onKeydown"]
const _hoisted_8 = {
  key: 0,
  class: "absolute left-2 top-2 z-10 rounded-full bg-[rgba(22,22,29,0.78)] px-[7px] py-0.5 text-[10.5px] font-bold tracking-wide text-white"
}
const _hoisted_9 = {
  key: 2,
  class: "mx-3 mb-3 flex items-start gap-2 rounded-lg bg-[#E3EEF7] px-2.5 py-2 text-xs text-[#2F6F9F]"
}
const _hoisted_10 = { class: "inline-flex items-center gap-1.5" }
const _hoisted_11 = { class: "inline-flex flex-none items-center gap-2" }
const _hoisted_12 = ["disabled"]
const _hoisted_13 = {
  key: 0,
  "aria-hidden": "true"
}
const _hoisted_14 = ["disabled"]
const _hoisted_15 = {
  class: "fixed inset-0 z-[60] flex flex-col bg-black/90 p-3",
  role: "dialog",
  "aria-label": "Anexo em tela cheia",
  "data-testid": "send-record-attachment-fullscreen"
}
const _hoisted_16 = { class: "mt-2 flex-1 overflow-auto" }

import { computed, nextTick, ref, watch, onBeforeUnmount } from 'vue'
import { mdiInformationOutline, mdiMagnifyPlusOutline, mdiOpenInNew, mdiRotateRight } from '@mdi/js'
import StepPreview from '@/components/designSystem/modals/AttendanceUploadModal/StepPreview.vue'

/**
 * Painel do anexo das etapas 4 e 5 do modal "Enviar registro" (M6, balões ④ e ⑩).
 *
 * O "cara-crachá" do emissor: o arquivo enviado na etapa 3 fica à vista enquanto
 * ele declara o horário, em vez de obrigá-lo a VOLTAR uma etapa para conferir.
 * A renderização em si é do `StepPreview.vue` do wizard de folha — imagem e PDF,
 * zoom e arrasto já resolvidos —, com a barra interna escondida porque este
 * painel oferece as mesmas ferramentas no próprio cabeçalho.
 */

const COMPACT_ZOOM = 2


export default /*#__PURE__*/_defineComponent({
  __name: 'AttachmentPanel',
  props: {
    files: {},
    layout: {},
    compact: { type: Boolean, default: false },
    toleranceHint: { default: null },
    activeIndex: { default: null }
  },
  emits: ["update:activeIndex"],
  setup(__props: any, { emit }) {

const props = __props;





/** Zoom do recorte quando não há marcação da etapa 3: o centro do anexo, 2x (⑫). */
const index = ref(0)
const rotation = ref(0)
const zoom = ref(1)
const isFullscreen = ref(false)
const track = ref<HTMLElement | null>(null)

/**
 * "Ver inteiro" desfaz o recorte SEM fechar o teclado (os botões usam
 * `@mousedown.prevent`, então o input não perde o foco). O estado volta sozinho
 * assim que o campo é desfocado e o `compact` cai.
 */
/**
 * A rolagem suave dispara dezenas de eventos de `scroll` ANTES de chegar ao
 * slide pedido, e cada um deles ainda lê a posição antiga. Sem esta trava o
 * `syncIndexWithScroll` desfazia o clique em "próximo" no meio da animação.
 */
let programmaticScroll: ReturnType<typeof setTimeout> | null = null

const seeFull = ref(false)
const isCompact = computed(() => props.compact && !seeFull.value)

watch(
  () => props.compact,
  (compact) => {
    if (!compact) seeFull.value = false
  }
)

// Ao voltar do recorte a faixa é remontada no começo: o anexo que estava na tela
// volta para debaixo da dobra, com o zoom e a rotação que tinham sido aplicados.
watch(isCompact, (compact) => {
  if (!compact) void nextTick(scrollToIndex)
})

// Trocar de arquivo zera zoom e rotação: eles são do anexo que estava na tela.
watch(index, () => {
  rotation.value = 0
  zoom.value = 1
})

watch(
  () => props.files.length,
  (total) => {
    if (index.value > total - 1) index.value = Math.max(0, total - 1)
  }
)

watch(
  () => props.activeIndex,
  (active) => {
    if (typeof active !== 'number') return
    const clamped = Math.min(Math.max(active, 0), Math.max(0, props.files.length - 1))
    if (clamped !== index.value) {
      index.value = clamped
      void nextTick(scrollToIndex)
    }
  },
  { immediate: true }
)

// Quem controla o índice de fora precisa acompanhar a paginação feita aqui dentro.
// Sem `activeIndex` o painel é autônomo, e um evento que ninguém pediu só faria ruído.
watch(index, (value) => {
  if (props.activeIndex !== null) emit('update:activeIndex', value)
})

const currentFile = computed<File | null>(() => props.files[index.value] ?? null)

/**
 * No recorte só o anexo atual entra na faixa — o "espiar o próximo" é do modo
 * inteiro. E só o slide visível e os vizinhos imediatos renderizam de verdade:
 * dez anexos na faixa seriam dez visualizadores vivos ao mesmo tempo.
 */
const slides = computed(() =>
  (isCompact.value ? [currentFile.value] : props.files)
    .filter((file): file is File => file !== null)
    .map((file, position) => ({
      key: `${position}-${file.name}`,
      file,
      render: isCompact.value || Math.abs(position - index.value) <= 1
    }))
)

const headerLabel = computed(() => {
  const total = props.files.length
  const name = isCompact.value ? 'recorte do horário' : (currentFile.value?.name ?? '')
  return `Anexo ${index.value + 1} de ${total}${name ? ` · ${name}` : ''}`
})

const pagerSuffix = computed(() =>
  props.layout === 'carousel' && !isCompact.value && props.files.length > 1
    ? ' · deslize para o próximo'
    : ''
)

/**
 * Object URL só do anexo aberto e só onde ele é usado: o "abrir em nova aba"
 * existe apenas no painel de desktop. No carrossel o `StepPreview` já cria (e
 * revoga) o dele, e um segundo URL por anexo seria memória presa à toa.
 */
const currentUrl = ref<string | null>(null)

function releaseUrl(): void {
  if (currentUrl.value) URL.revokeObjectURL(currentUrl.value)
  currentUrl.value = null
}

watch(
  [currentFile, () => props.layout],
  ([file, layout]) => {
    releaseUrl()
    if (layout !== 'side') return
    if (file && typeof URL.createObjectURL === 'function') {
      currentUrl.value = URL.createObjectURL(file)
    }
  },
  { immediate: true }
)

onBeforeUnmount(() => {
  releaseUrl()
  if (programmaticScroll) clearTimeout(programmaticScroll)
})

function rotate(): void {
  rotation.value = (rotation.value + 90) % 360
}

/** Um botão só: "Ampliar" cicla 1x → 1,5x → … → 4x e volta ao tamanho natural. */
function zoomIn(): void {
  zoom.value = zoom.value >= 4 ? 1 : Math.round((zoom.value + 0.5) * 10) / 10
}

function go(step: number): void {
  const next = index.value + step
  if (next < 0 || next > props.files.length - 1) return
  index.value = next
  scrollToIndex()
}

function scrollToIndex(): void {
  const element = track.value
  const slide = element?.firstElementChild as HTMLElement | null
  if (!element || !slide || !slide.offsetWidth || typeof element.scrollTo !== 'function') return
  if (programmaticScroll) clearTimeout(programmaticScroll)
  programmaticScroll = setTimeout(() => {
    programmaticScroll = null
  }, 400)
  element.scrollTo({ left: index.value * (slide.offsetWidth + 8), behavior: 'smooth' })
}

/** O dedo manda: o índice segue o slide que parou sob a dobra da faixa. */
function syncIndexWithScroll(): void {
  // No recorte a faixa tem UM slide e o `scrollLeft` é sempre 0: ler a posição
  // aqui jogaria o índice de volta ao primeiro anexo assim que o teclado abrisse.
  if (isCompact.value || programmaticScroll) return
  const element = track.value
  const slide = element?.firstElementChild as HTMLElement | null
  if (!element || !slide || !slide.offsetWidth) return
  const position = Math.round(element.scrollLeft / (slide.offsetWidth + 8))
  const clamped = Math.min(Math.max(position, 0), props.files.length - 1)
  if (clamped !== index.value) index.value = clamped
}

function openFullscreen(): void {
  if (props.layout !== 'carousel' || isCompact.value) return
  isFullscreen.value = true
}

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["rounded-[10px] border border-lightGray30 bg-[#FAFBFC]", _ctx.layout === 'side' ? 'md:sticky md:top-3' : '']),
    "data-testid": "send-record-attachment-panel"
  }, [
    (!_ctx.files.length)
      ? (_openBlock(), _createElementBlock("p", _hoisted_1, "Nenhum anexo neste registro"))
      : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
          _createElementVNode("div", {
            class: _normalizeClass(["flex items-center justify-between gap-2 px-3 py-2", _ctx.layout === 'side' ? 'border-b border-gray-100' : ''])
          }, [
            _createElementVNode("b", _hoisted_2, _toDisplayString(headerLabel.value), 1),
            _createElementVNode("span", _hoisted_3, [
              _createElementVNode("button", {
                type: "button",
                title: "Girar",
                "aria-label": "Girar anexo",
                class: "flex h-[26px] w-[26px] items-center justify-center rounded-md border border-lightGray30 bg-white text-neutral50",
                "data-testid": "send-record-attachment-rotate",
                onMousedown: _cache[0] || (_cache[0] = _withModifiers(() => {}, ["prevent"])),
                onClick: rotate
              }, [
                _createVNode(_component_v_icon, {
                  icon: _unref(mdiRotateRight),
                  size: "14"
                }, null, 8, ["icon"])
              ], 32),
              _createElementVNode("button", {
                type: "button",
                title: "Ampliar",
                "aria-label": "Ampliar anexo",
                class: "flex h-[26px] w-[26px] items-center justify-center rounded-md border border-lightGray30 bg-white text-neutral50",
                "data-testid": "send-record-attachment-zoom",
                onMousedown: _cache[1] || (_cache[1] = _withModifiers(() => {}, ["prevent"])),
                onClick: zoomIn
              }, [
                _createVNode(_component_v_icon, {
                  icon: _unref(mdiMagnifyPlusOutline),
                  size: "14"
                }, null, 8, ["icon"])
              ], 32),
              (_ctx.layout === 'side' && currentUrl.value)
                ? (_openBlock(), _createElementBlock("a", {
                    key: 0,
                    href: currentUrl.value,
                    target: "_blank",
                    rel: "noopener noreferrer",
                    title: "Abrir em nova aba",
                    "aria-label": "Abrir anexo em nova aba",
                    class: "flex h-[26px] w-[26px] items-center justify-center rounded-md border border-lightGray30 bg-white text-neutral50",
                    "data-testid": "send-record-attachment-open"
                  }, [
                    _createVNode(_component_v_icon, {
                      icon: _unref(mdiOpenInNew),
                      size: "14"
                    }, null, 8, ["icon"])
                  ], 8, _hoisted_4))
                : _createCommentVNode("", true)
            ])
          ], 2),
          (_ctx.layout === 'side')
            ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                _createVNode(StepPreview, {
                  file: currentFile.value,
                  mode: "preview",
                  "hide-toolbar": "",
                  "show-display-name": false,
                  "height-px": 360,
                  rotation: rotation.value,
                  zoom: zoom.value,
                  "onUpdate:rotation": _cache[2] || (_cache[2] = ($event: any) => (rotation.value = $event)),
                  "onUpdate:zoom": _cache[3] || (_cache[3] = ($event: any) => (zoom.value = $event))
                }, null, 8, ["file", "rotation", "zoom"])
              ]))
            : (_openBlock(), _createElementBlock("div", {
                key: 1,
                class: "px-3 pb-2",
                "data-testid": 
          isCompact.value ? 'send-record-attachment-compact' : 'send-record-attachment-carousel'
        
              }, [
                _createElementVNode("div", {
                  ref_key: "track",
                  ref: track,
                  class: "flex gap-2 overflow-x-auto snap-x snap-mandatory attachment-track",
                  onScrollPassive: syncIndexWithScroll
                }, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(slides.value, (slide) => {
                    return (_openBlock(), _createElementBlock("div", {
                      key: slide.key,
                      class: _normalizeClass(["relative flex-none snap-center overflow-hidden rounded-lg", isCompact.value ? 'basis-full' : 'basis-[88%]']),
                      role: isCompact.value ? undefined : 'button',
                      tabindex: isCompact.value ? undefined : 0,
                      "aria-label": isCompact.value ? undefined : `Abrir ${slide.file.name} em tela cheia`,
                      onClick: openFullscreen,
                      onKeydown: [
                        _withKeys(_withModifiers(openFullscreen, ["prevent"]), ["enter"]),
                        _withKeys(_withModifiers(openFullscreen, ["prevent"]), ["space"])
                      ]
                    }, [
                      (isCompact.value)
                        ? (_openBlock(), _createElementBlock("span", _hoisted_8, " RECORTE · HORÁRIO "))
                        : _createCommentVNode("", true),
                      (slide.render)
                        ? (_openBlock(), _createBlock(StepPreview, {
                            key: 1,
                            file: slide.file,
                            mode: "preview",
                            "hide-toolbar": "",
                            "show-display-name": false,
                            "height-px": isCompact.value ? 96 : 170,
                            rotation: rotation.value,
                            zoom: isCompact.value ? COMPACT_ZOOM : zoom.value,
                            "onUpdate:rotation": _cache[4] || (_cache[4] = ($event: any) => (rotation.value = $event)),
                            "onUpdate:zoom": _cache[5] || (_cache[5] = ($event: any) => (zoom.value = $event))
                          }, null, 8, ["file", "height-px", "rotation", "zoom"]))
                        : (_openBlock(), _createElementBlock("div", {
                            key: 2,
                            class: "w-full bg-lightGray30/40",
                            style: _normalizeStyle({ height: `${isCompact.value ? 96 : 170}px` }),
                            "aria-hidden": "true"
                          }, null, 4))
                    ], 42, _hoisted_7))
                  }), 128))
                ], 544)
              ], 8, _hoisted_6)),
          (_ctx.toleranceHint)
            ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                _createVNode(_component_v_icon, {
                  icon: _unref(mdiInformationOutline),
                  size: "14",
                  class: "mt-0.5 flex-none"
                }, null, 8, ["icon"]),
                _createElementVNode("span", null, _toDisplayString(_ctx.toleranceHint), 1)
              ]))
            : _createCommentVNode("", true),
          _createElementVNode("div", {
            class: _normalizeClass(["flex items-center justify-between gap-2 px-3 pb-2.5 text-xs text-neutral50", _ctx.layout === 'carousel' ? 'pt-0' : ''])
          }, [
            _createElementVNode("span", _hoisted_10, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.files, (file, position) => {
                return (_openBlock(), _createElementBlock("i", {
                  key: `dot-${position}-${file.name}`,
                  class: _normalizeClass(["h-1.5 w-1.5 rounded-full", position === index.value ? 'bg-primary30' : 'bg-lightGray30'])
                }, null, 2))
              }), 128)),
              _createTextVNode(" " + _toDisplayString(index.value + 1) + " / " + _toDisplayString(_ctx.files.length) + _toDisplayString(pagerSuffix.value), 1)
            ]),
            _createElementVNode("span", _hoisted_11, [
              (isCompact.value)
                ? (_openBlock(), _createElementBlock("button", {
                    key: 0,
                    type: "button",
                    class: "font-bold text-primary30",
                    "data-testid": "send-record-attachment-see-full",
                    onMousedown: _cache[6] || (_cache[6] = _withModifiers(() => {}, ["prevent"])),
                    onClick: _cache[7] || (_cache[7] = ($event: any) => (seeFull.value = true))
                  }, " Ver inteiro ", 32))
                : _createCommentVNode("", true),
              (_ctx.files.length > 1)
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                    _createElementVNode("button", {
                      type: "button",
                      class: "font-bold text-primary30 disabled:opacity-40",
                      disabled: index.value === 0,
                      "data-testid": "send-record-attachment-prev",
                      onMousedown: _cache[8] || (_cache[8] = _withModifiers(() => {}, ["prevent"])),
                      onClick: _cache[9] || (_cache[9] = ($event: any) => (go(-1)))
                    }, " ‹ anterior ", 40, _hoisted_12),
                    (_ctx.layout === 'side')
                      ? (_openBlock(), _createElementBlock("span", _hoisted_13, "·"))
                      : _createCommentVNode("", true),
                    _createElementVNode("button", {
                      type: "button",
                      class: "font-bold text-primary30 disabled:opacity-40",
                      disabled: index.value === _ctx.files.length - 1,
                      "data-testid": "send-record-attachment-next",
                      onMousedown: _cache[10] || (_cache[10] = _withModifiers(() => {}, ["prevent"])),
                      onClick: _cache[11] || (_cache[11] = ($event: any) => (go(1)))
                    }, " próximo › ", 40, _hoisted_14)
                  ], 64))
                : _createCommentVNode("", true)
            ])
          ], 2)
        ], 64)),
    (isFullscreen.value)
      ? (_openBlock(), _createBlock(_Teleport, {
          key: 2,
          to: "body"
        }, [
          _createElementVNode("div", _hoisted_15, [
            _createElementVNode("button", {
              type: "button",
              class: "self-end rounded-md bg-white px-3 py-1 text-sm font-bold text-primary30",
              "data-testid": "send-record-attachment-fullscreen-close",
              onClick: _cache[12] || (_cache[12] = ($event: any) => (isFullscreen.value = false))
            }, " Fechar "),
            _createElementVNode("div", _hoisted_16, [
              _createVNode(StepPreview, {
                file: currentFile.value,
                mode: "preview",
                "show-display-name": false,
                rotation: rotation.value,
                zoom: zoom.value,
                "onUpdate:rotation": _cache[13] || (_cache[13] = ($event: any) => (rotation.value = $event)),
                "onUpdate:zoom": _cache[14] || (_cache[14] = ($event: any) => (zoom.value = $event))
              }, null, 8, ["file", "rotation", "zoom"])
            ])
          ])
        ]))
      : _createCommentVNode("", true)
  ], 2))
}
}

})